import { makeAutoObservable } from 'mobx';
import {
	EntityAPI,
	EntityType,
	IEntityAPI,
	IEntityColumn,
	IEntityDashboardConfigurationTemplate,
	IEntityTypeUserInfo,
	IGenericEntity,
	ITaskRequestFilterField,
	IUserConfigPackage,
	IViewInfo,
} from '../API';
import { ViewsState } from '../components/controls/ViewsCtrl';
import { FiltersState } from './FiltersState';
import VersionHistoryState from './VersionHistoryState';
import { PopupWindow } from './PopupWindow';
import { EntityDetailsState } from '../components/controls/EntityDetails';

export class EntityDashboardListItemState {
	item: IGenericEntity;
	isFavorited: boolean;
	isSelected:boolean;

	constructor(item: IGenericEntity) {
		this.item = item;
		this.isFavorited = false;
		this.isSelected = false;

		makeAutoObservable(this);
	}

	public setIsFavourited(val:boolean) {
		this.isFavorited = val;
	}

	public setIsSelected(val:boolean) {
		this.isSelected = val;
	}

	public getFieldValue(fieldName: string): any {
		if(!fieldName)
			return undefined;
		return (this.item as any)[fieldName];
	}
}

export default class EntityDashboardState {
	public loaded: boolean = false;
	public loading: boolean = false;

	public searchText: string = '';
	public pageLink: string = '';
	public sortField: string = '';
	public sortAsc: boolean = true;
	public isSearchable: boolean;

	public timeTravelDate: Date | null = null;

	private getApi: () => Promise<IEntityAPI<IGenericEntity>>;

	public items: EntityDashboardListItemState[] = [];
	public pinnedIds: Set<string> = new Set<string>();

	public filtersState: FiltersState;
	public allColumns: IEntityColumn[] = [];

	//The columns which are shown in the DetailsList
	public tableDisplayColumnInternalNames: string[] = [];

	public versionHistoryState: VersionHistoryState | null = null;

	public showColumnSelector: boolean = false;
	public viewsState: ViewsState;
	showItemVersions: boolean;
	userConfig: IUserConfigPackage;
	private preloadedIds: string[] | null = null;

	public showDeleteConfirmDialog: boolean = false;
	entityTypeConfig: IEntityTypeUserInfo;
	entityDialog: EntityDetailsState;

	constructor(
		entityType: string | EntityType,
		userConfig: IUserConfigPackage,
		entityDialog: EntityDetailsState,
		showItemVersions: boolean = true,
		allowSearching: boolean = true
	) {
		makeAutoObservable(this);

		this.entityDialog = entityDialog;
		this.entityTypeConfig = userConfig.entityTypes.getConfig(entityType) as IEntityTypeUserInfo;

		this.getApi = async () => {
			return await EntityAPI.fromEntityTypeOrId(entityType, userConfig);
		};

		this.userConfig = userConfig;
		this.isSearchable = allowSearching;
		this.showItemVersions = showItemVersions;

		this.viewsState = new ViewsState(
			'Report',
			this.getApi,
			this.setSelectedView.bind(this),
			() => '',
			this.getCurrentViewConfig.bind(this)
		);
		this.filtersState = new FiltersState(
			userConfig,
			this.getApi,
			(newFilters) => {
				this.setFilters(newFilters);
				this.load();
			},
		);
	}

	public showEntityDialog(item: EntityDashboardListItemState|null) {
		if(item) {
			this.entityDialog.show(this.entityTypeConfig.id, item.item, this.allColumns);
		}
	}

	public showNewForm() {
		PopupWindow.show(this.entityTypeConfig?.sharePointListNewFormUrl || '').then(() => {
			//Do something when the popup closed
		});
	}

	public async confirmSelectedItemDeletion(): Promise<void> {
		let selectedIds = this.items.filter((i) => i.isSelected).map((i) => i.item.Id);
		let api = await this.getApi();
		await api.delete(selectedIds);
		this.removeDeletedItems(selectedIds);
		this.toggleDeleteConfirmDialog(false);
	}

	public toggleDeleteConfirmDialog(arg0: boolean) {
		this.showDeleteConfirmDialog = arg0;
	}

	public getConfig(): IEntityTypeUserInfo {
		return this.entityTypeConfig;
	}

	//For search results, we only want to show pre-selected IDs
	public setSearchResultIds(ids: string[]) {
		this.preloadedIds = ids;
	}

	public toggleShowColumnSelector() {
		this.showColumnSelector = !this.showColumnSelector;
	}

	public setTableDisplayColumns(columns: string[]) {
		this.tableDisplayColumnInternalNames = columns;
	}

	public setColumnViewable(columnName: string, viewable: boolean) {
		if (viewable) {
			this.tableDisplayColumnInternalNames =
				this.tableDisplayColumnInternalNames.concat([columnName]);
		} else {
			this.tableDisplayColumnInternalNames =
				this.tableDisplayColumnInternalNames.filter((c) => c !== columnName);
		}
	}

	public removeDeletedItems(ids: string[]) {
		this.items = this.items.filter((f) => ids.indexOf(f.item.Id) < 0);
	}

	public setSelection(items: EntityDashboardListItemState[]) {
		this.items.forEach(item => {
			item.setIsSelected(items.includes(item));
		});
	}

	//Called when the control loads, and data can be refreshed at that point if needs be.
	public async ensureLoaded() {
		if (!this.loaded && !this.loading) {
			//Perform initial load of columns
			this.allColumns = await (await this.getApi()).columns();

			this.filtersState.setFilterFields(
				this.allColumns.filter((c) => (c.id || '').toLowerCase() !== 'title'),
			);

			this.versionHistoryState = this.showItemVersions
				? new VersionHistoryState(this.userConfig, this.getApi, this.allColumns)
				: null;

			await this.load();
		}
	}

	//Sets standard filters which are also controlled by views and the filter panel
	public setFilters(filters: ITaskRequestFilterField[]) {
		this.items = [];
		this.filtersState.setFilters(filters);
		this.pageLink = '';
		this.loaded = false;
	}

	public setTimeTravelDate(date: Date | null) {
		this.timeTravelDate = date;
		this.reload();
	}

	public setSorting(fieldName: string, sortAsc: boolean) {
		this.sortField = fieldName;
		this.sortAsc = sortAsc;
		this.reload();
	}

	public setSearchText(searchText: string) {
		this.searchText = searchText;
		this.reload();
	}

	public async reload() {
		if (!this.loaded) {
			//If we haven't loaded in the first place, no need to reload.
			return;
		}

		this.items = [];
		this.pageLink = '';
		await this.load();
	}

	public async togglePin(item: EntityDashboardListItemState): Promise<void> {
		item.setIsFavourited(!item.isFavorited);
		const api = await this.getApi();
		if(item.isFavorited) {
			await api.pin(item.item.Id);
			this.pinnedIds.add(item.item.Id);
		} else {
			await api.unpin(item.item.Id);
			this.pinnedIds.delete(item.item.Id);
		}
	}

	public async load(): Promise<void> {
		this.loading = true;
		let api = await this.getApi();
		let result = await api.query({
			parentId: '',
			searchText: this.searchText,
			ids: this.preloadedIds || [],
			queryTypes: [],
			pageLink: this.pageLink,
			filterFields: this.filtersState.getAllFilters(),
			sortField: this.sortField,
			sortAsc: this.sortAsc,
			pageSize: 100,
			modifiedSince: '',
			viewId: this.viewsState.selected?.id || '',
			timeTravel: this.timeTravelDate ? this.timeTravelDate.toISOString() : '',
		});

		if (result) {
			let newItemObjects = result.items.map((i) => new EntityDashboardListItemState(i));
			this.items = this.pageLink ? this.items.concat(newItemObjects) : newItemObjects;
			this.pageLink = result.nextPageLink;
			const pinned = await api.pinnedIds();
			this.pinnedIds = new Set<string>(pinned);

			newItemObjects.forEach(item => {
				item.setIsFavourited(this.pinnedIds.has(item.item.Id));
			});
		}

		this.loading = false;
		this.loaded = true;
	}

	public getCurrentViewConfig(): IEntityDashboardConfigurationTemplate {
		return {
			tableDisplayColumnInternalNames: this.tableDisplayColumnInternalNames,
			filterFields: this.filtersState.selectedFilters,
			sortField: this.sortField,
			sortAsc: this.sortAsc,
		};
	}

	public async setSelectedView(t: IViewInfo) {
		if (t) {
			this.setTableDisplayColumns(t.config.tableDisplayColumnInternalNames);
			this.sortField = t.config.sortField;
			this.sortAsc = t.config.sortAsc;
			this.filtersState.setFilters(t.config.filterFields);
			this.filtersState.applyFilters();
		}
	}
}
